import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Select, Spinner, RNLLoader } from '../UI';
import { SortTh, useSortState, sortRows } from '../requisition/SortableHeader';
import { PinnedDetail, useScrollViewport } from '../requisition/PinnedDetail';
import * as api from '../../api';
import { fmt, fmtDate } from './mpoUtils';
import MPOStatusPill, { MPO_PILL } from './MPOStatusPill';
import MPODetailView from './MPODetailView';
import MPOForm from './MPOForm';
import MPOPrintButton from './MPOPrintButton';
import MPODownloadPDFButton from './MPODownloadPDFButton';
import Pagination from '../requisition/Pagination';

const ic = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IcoEye     = () => <svg {...ic}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const IcoPencil  = () => <svg {...ic}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IcoRefresh = () => <svg {...ic}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
const IcoSearch  = () => <svg {...ic}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;

function Field({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontSize: 12.5, color: '#1e293b', fontWeight: 600, lineHeight: 1.4 }}>{value ?? '—'}</span>
    </div>
  );
}

const panel = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '12px 16px' };
const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px 20px' };

function ExpandedDetail({ po, fullPO, loading }) {
  const items = fullPO?.items || [];
  return (
    <div
      style={{ background: '#f1f5f9', borderTop: '2px solid #3b82f6', borderBottom: '2px solid #cbd5e1' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '8px 20px', background: '#e8eef8', borderBottom: '1px solid #cbd5e1', fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        PO: {po.po_number || po.serial_number} &mdash; {po.supplier_name || 'Unknown Supplier'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 20px 16px' }}>
        <div style={panel}>
          <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
            Section 1 — Purchase Order Header
          </div>
          <div style={grid4}>
            <Field label="PO Number"        value={<span style={{ fontFamily: 'monospace', color: '#1e40af' }}>{po.po_number || '—'}</span>} />
            <Field label="Serial No."       value={<span style={{ fontFamily: 'monospace', color: '#475569' }}>{po.serial_number}</span>} />
            <Field label="Project"           value={po.project_name || '—'} />
            <Field label="Supplier"          value={po.supplier_name || '—'} />
            <Field label="Currency"          value={po.currency || 'NGN'} />
            <Field label="PO Date"           value={fmtDate(po.po_date)} />
            <Field label="Expected Delivery" value={fmtDate(po.expected_delivery_date)} />
            <Field label="Payment Terms"     value={po.payment_terms || '—'} />
            <Field label="Delivery Terms"    value={po.delivery_terms || '—'} />
            <Field label="Submitted By"      value={po.submitted_by_name || '—'} />
            <Field label="Submitted Date"    value={fmtDate(po.submitted_date)} />
            <Field label="Grand Total"       value={
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e40af', fontSize: 13 }}>
                ₦{(parseFloat(po.grand_total) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
              </span>
            } />
            {loading && <Field label="Line Items" value={<span style={{ color: '#94a3b8' }}>Loading…</span>} />}
            {!loading && fullPO && <Field label="Line Items" value={`${items.length} item${items.length !== 1 ? 's' : ''}`} />}
          </div>
          {po.notes && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
              <Field label="Notes" value={po.notes} />
            </div>
          )}
        </div>
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13, padding: '8px 0' }}>
            <Spinner size={15} /> Loading line items…
          </div>
        )}
        {!loading && items.length > 0 && (
          <div>
            <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
              Line Items as Ordered
            </div>
          </div>
        )}
      </div>
      {!loading && items.length > 0 && (
        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto', borderTop: '1px solid #cbd5e1' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13.5, minWidth: 2400 }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                {[
                  { h: '#',             w: 50  }, { h: 'Item Code',     w: 160 },
                  { h: 'Description',   w: 260 }, { h: 'New Item Desc', w: 240 },
                  { h: 'Specification', w: 220 }, { h: 'Warehouse',     w: 160 },
                  { h: 'Request Ref',   w: 180 }, { h: 'BOQ Code',      w: 160 },
                  { h: 'Rem. Rqst Qty', w: 150 }, { h: 'Unit',          w: 100 },
                  { h: 'Qty',           w: 120 }, { h: 'Last Pur.Pr',   w: 160 },
                  { h: 'Price (₦)',     w: 160 }, { h: 'Disc %',        w: 100 },
                  { h: 'VAT %',         w: 100 }, { h: 'Total (₦)',     w: 180 },
                  { h: 'VAT Amt (₦)',   w: 160 }, { h: 'Remark',        w: 200 },
                ].map(({ h, w }) => (
                  <th key={h} style={{ padding: '11px 14px', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 700, textAlign: 'center', fontSize: 12.5, whiteSpace: 'nowrap', minWidth: w }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const td  = { padding: '10px 14px', border: '1px solid #e2e8f0', textAlign: 'center', whiteSpace: 'nowrap', fontSize: 13 };
                const num = { ...td, fontFamily: 'monospace' };
                return (
                  <tr key={item.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ ...td, color: '#94a3b8', fontWeight: 700 }}>{item.line_number || i + 1}</td>
                    <td style={{ ...td, color: '#475569' }}>{item.material_code || '—'}</td>
                    <td style={{ ...td, fontWeight: 600, color: '#1e293b', textAlign: 'left', whiteSpace: 'normal' }}>{item.material_description || '—'}</td>
                    <td style={{ ...td, color: '#475569', textAlign: 'left', whiteSpace: 'normal' }}>{item.new_item_description || '—'}</td>
                    <td style={{ ...td, color: '#475569', textAlign: 'left', whiteSpace: 'normal' }}>{item.specification || '—'}</td>
                    <td style={td}>{item.warehouse || '—'}</td>
                    <td style={td}>{item.request_ref || '—'}</td>
                    <td style={td}>{item.boq_item_code || '—'}</td>
                    <td style={num}>{item.rem_rqst_qty != null ? fmt(item.rem_rqst_qty) : '—'}</td>
                    <td style={td}>{item.unit_of_measure || '—'}</td>
                    <td style={num}>{fmt(item.quantity_ordered)}</td>
                    <td style={num}>{item.last_pur_pr != null ? fmt(item.last_pur_pr) : '—'}</td>
                    <td style={num}>{fmt(item.unit_rate)}</td>
                    <td style={num}>{item.disc != null ? `${item.disc}%` : '—'}</td>
                    <td style={num}>{item.vat_percentage != null ? `${item.vat_percentage}%` : '—'}</td>
                    <td style={{ ...num, fontWeight: 700, color: '#1e40af' }}>{fmt(item.amount)}</td>
                    <td style={{ ...num, color: '#475569' }}>{item.vat_amount != null ? fmt(item.vat_amount) : '—'}</td>
                    <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal', color: '#64748b' }}>{item.remarks || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#e8eef8' }}>
                <td colSpan={15} style={{ padding: '11px 14px', border: '1px solid #e2e8f0', fontWeight: 700, textAlign: 'right', fontSize: 13 }}>Grand Total</td>
                <td style={{ padding: '11px 14px', border: '1px solid #e2e8f0', textAlign: 'center', fontFamily: 'monospace', fontWeight: 800, color: '#1e40af', fontSize: 13.5 }}>
                  ₦{(parseFloat(po.grand_total) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </td>
                <td colSpan={2} style={{ padding: '11px 14px', border: '1px solid #e2e8f0' }} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const MY_ORDERS_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft',              label: 'Draft' },
  { value: 'pending_approval',   label: 'Pending Approval' },
  { value: 'returned',           label: 'Returned' },
  { value: 'rejected',           label: 'Rejected' },
  { value: 'approved',           label: 'Approved' },
  { value: 'issued',             label: 'Issued' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'fully_received',     label: 'Fully Received' },
  { value: 'cancelled',          label: 'Cancelled' },
  { value: 'closed',             label: 'Closed' },
];

export default function MPOMyOrders() {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [error, setError]             = useState(null);
  const [view, setView]               = useState('list');
  const [selectedPO, setSelectedPO]   = useState(null);
  const [editPO, setEditPO]           = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [page, setPage]               = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount]   = useState(0);
  const [statCounts, setStatCounts]   = useState({ draft: 0, pending_approval: 0, returned: 0, rejected: 0, approved: 0 });

  const [filterStatus, setFilterStatus] = useState('');
  const [searchInput, setSearchInput]   = useState('');
  const [search, setSearch]             = useState('');

  const [expandedRows, setExpandedRows] = useState({});
  const [poCache, setPoCache]           = useState({});
  const [poLoading, setPoLoading]       = useState({});

  const sortState = useSortState('created_at', 'desc');
  const { ref: tableRef, width: tableWidth } = useScrollViewport([view]);

  useEffect(() => {
    api.getMe()
      .then(me => { if (me?.user_id) setCurrentUserId(me.user_id); })
      .catch(() => {})
      .finally(() => setUserLoading(false));
  }, []);

  const loadStats = useCallback(() => {
    if (!currentUserId) return;
    Promise.all([
      api.listMPOs({ created_by_id: currentUserId, status: 'draft',            limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ created_by_id: currentUserId, status: 'pending_approval', limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ created_by_id: currentUserId, status: 'returned',         limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ created_by_id: currentUserId, status: 'rejected',         limit: 1 }).catch(() => ({ total: 0 })),
      api.listMPOs({ created_by_id: currentUserId, status: 'approved',         limit: 1 }).catch(() => ({ total: 0 })),
    ]).then(([d, p, r, x, a]) => setStatCounts({
      draft:            d?.total || 0,
      pending_approval: p?.total || 0,
      returned:         r?.total || 0,
      rejected:         x?.total || 0,
      approved:         a?.total || 0,
    })).catch(() => {});
  }, [currentUserId]);

  useEffect(() => { if (currentUserId) loadStats(); }, [loadStats]);

  const commitSearch = () => {
    setSearch(searchInput.trim());
    setPage({ skip: 0, limit: 10 });
  };

  const load = useCallback(() => {
    if (!currentUserId) return;
    setLoading(true);
    setError(null);
    api.listMPOs({
      created_by_id: currentUserId,
      limit:  page.limit,
      offset: page.skip,
      ...(filterStatus && { status: filterStatus }),
      ...(search       && { search }),
    })
      .then(res => {
        setRows(res?.items || []);
        setTotalCount(res?.total || 0);
      })
      .catch(e => setError(e.message || 'Failed to load your orders.'))
      .finally(() => setLoading(false));
  }, [currentUserId, page, filterStatus, search]);

  useEffect(() => { if (view === 'list' && currentUserId) load(); }, [load, view, currentUserId]);

  const toggleExpanded = async (e, poId) => {
    e.stopPropagation();
    const opening = !expandedRows[poId];
    setExpandedRows(prev => ({ ...prev, [poId]: opening }));
    if (opening) api.markEntitySeen(poId, 'material_po').catch(() => {});
    if (opening && !poCache[poId]) {
      setPoLoading(prev => ({ ...prev, [poId]: true }));
      try {
        const full = await api.getMPO(poId);
        setPoCache(prev => ({ ...prev, [poId]: full }));
      } catch {}
      finally {
        setPoLoading(prev => ({ ...prev, [poId]: false }));
      }
    }
  };

  const openDetail = async (e, po) => {
    e.stopPropagation();
    setDetailLoading(true);
    try {
      const full = await api.getMPO(po.po_id);
      setSelectedPO(full);
      setView('detail');
    } catch (err) {
      setError(err.message || 'Failed to load PO.');
    } finally {
      setDetailLoading(false);
    }
  };

  // Draft and returned POs open in the PO form right here; saving or resubmitting comes back to this list.
  const openEdit = async (e, po) => {
    e?.stopPropagation?.();
    setDetailLoading(true);
    try {
      const full = po.items ? po : await api.getMPO(po.po_id);
      setEditPO(full);
      setView('form');
    } catch (err) {
      setError(err.message || 'Failed to load PO for edit.');
    } finally {
      setDetailLoading(false);
    }
  };

  const backToList = () => {
    setEditPO(null);
    setSelectedPO(null);
    setPoCache({});
    setView('list');
    loadStats();
  };

  const sortedRows = useMemo(() =>
    sortRows(rows, sortState, (row, key) => {
      if (key === 'submitted_date') return row.submitted_date || '';
      if (key === 'created_at')     return row.created_at || '';
      if (key === 'grand_total')    return parseFloat(row.grand_total) || 0;
      if (key === 'po_number')      return row.po_number || '';
      if (key === 'project_name')   return row.project_name || '';
      if (key === 'supplier_name')  return row.supplier_name || '';
      return row[key] ?? '';
    }),
  [rows, sortState]);

  if (userLoading || (loading && rows.length === 0)) return <RNLLoader title="Loading your orders..." subtitle="Fetching your purchase orders" />;
  if (detailLoading) return <RNLLoader title="Loading purchase order…" subtitle="Please wait" />;

  if (view === 'form' && editPO) {
    return <MPOForm existing={editPO} onSaved={backToList} onCancel={backToList} />;
  }

  if (view === 'detail' && selectedPO) {
    return (
      <MPODetailView
        po={selectedPO}
        readOnly
        ownerActions
        onBack={backToList}
        onRefresh={async () => {
          try { const full = await api.getMPO(selectedPO.po_id); setSelectedPO(full); loadStats(); } catch {}
        }}
        onEdit={(po) => openEdit(null, po)}
      />
    );
  }

  const thStyle = { border: '1px solid black' };
  const tdB     = { padding: '11px 12px', border: '1px solid black', fontSize: 12.5, verticalAlign: 'middle', textAlign: 'center' };
  const mono    = { fontFamily: 'monospace', fontWeight: 700 };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontWeight: 800, fontSize: 18, color: 'var(--text-primary)' }}>
            My Orders
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            All purchase orders you created, across all statuses.
          </p>
        </div>
        <button
          onClick={load}
          title="Refresh"
          style={{ background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}
        >
          <IcoRefresh /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        {['draft', 'pending_approval', 'returned', 'rejected', 'approved'].map((st) => ({
          status: st, label: MPO_PILL[st].label, value: statCounts[st], color: MPO_PILL[st].col, bg: MPO_PILL[st].bg, border: `${MPO_PILL[st].col}55`,
        })).map(({ status: st, label, value, color, bg, border }) => (
          <div
            key={label}
            onClick={() => { setFilterStatus(filterStatus === st ? '' : st); setPage({ skip: 0, limit: 10 }); }}
            title={`Show ${label.toLowerCase()} orders`}
            style={{ background: bg, border: `${filterStatus === st ? 2 : 1}px solid ${border}`, borderRadius: 10, padding: '16px 20px', textAlign: 'center', cursor: 'pointer' }}
          >
            <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'monospace', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 6 }}>{label}</div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 12.5, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Filters + Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 180 }}>
          <Select
            label="Filter by Status" title="Select Status" value={filterStatus}
            options={MY_ORDERS_STATUS_OPTIONS} placeholder="All statuses" clearable
            onChange={val => { setFilterStatus(val); setPage({ skip: 0, limit: 10 }); }}
          />
        </div>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 220 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}><IcoSearch /></span>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && commitSearch()}
            placeholder="Search PO number, project, supplier…"
            style={{ width: '100%', height: 36, boxSizing: 'border-box', padding: '0 10px 0 34px', fontSize: 13, border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none' }}
          />
        </div>
        <button
          onClick={commitSearch}
          style={{ height: 36, padding: '0 18px', background: '#0b318b', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          Search
        </button>
        {(filterStatus || search) && (
          <button
            onClick={() => { setFilterStatus(''); setSearchInput(''); setSearch(''); setPage({ skip: 0, limit: 10 }); }}
            style={{ height: 36, padding: '0 14px', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div ref={tableRef} style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, border: '2px solid black' }}>
          <thead>
            <tr style={{ background: '#0b318bff', color: '#fff' }}>
              <SortTh label="PO NUMBER"      sortKey="po_number"      state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <SortTh label="CREATED DATE"   sortKey="created_at"     state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <SortTh label="SUBMITTED DATE" sortKey="submitted_date" state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <SortTh label="PROJECT"        sortKey="project_name"   state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <SortTh label="SUPPLIER"       sortKey="supplier_name"  state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <SortTh label="GRAND TOTAL"    sortKey="grand_total"    state={sortState} style={{ ...thStyle, textAlign: 'center' }} />
              <th style={{ ...thStyle, padding: '12px', fontWeight: 700, fontSize: 11, textAlign: 'center', whiteSpace: 'nowrap' }}>STATUS</th>
              <th style={{ ...thStyle, padding: '12px', fontWeight: 700, fontSize: 11, textAlign: 'center', whiteSpace: 'nowrap' }}>CURRENTLY WITH</th>
              <th style={{ ...thStyle, padding: '12px', fontWeight: 700, fontSize: 11, textAlign: 'center', whiteSpace: 'nowrap' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && !loading && (
              <tr><td colSpan={9} style={{ ...tdB, padding: '32px 20px', color: '#9ca3af' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{totalCount === 0 ? '📋' : '🔍'}</div>
                <div style={{ fontWeight: 600 }}>{totalCount === 0 ? "You haven't created any purchase orders yet" : 'No matching orders'}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{totalCount === 0 ? 'Go to the PO Landing tab to create one.' : 'Try clearing the filter.'}</div>
              </td></tr>
            )}
            {sortedRows.map((row, idx) => {
              const isExpanded  = !!expandedRows[row.po_id];
              const isEditable  = row.status === 'draft' || row.status === 'returned';
              const isRejected  = row.status === 'rejected';
              const showPrintPdf = !isEditable && !isRejected;
              return (
                <React.Fragment key={row.po_id}>
                  <tr
                    onClick={(e) => toggleExpanded(e, row.po_id)}
                    style={{ background: isExpanded ? '#dbeafe' : (idx % 2 === 0 ? '#fff' : '#f8fafc'), cursor: 'pointer', transition: 'background 0.15s' }}
                  >
                    {/* 1 — PO NUMBER */}
                    <td style={{ ...tdB, textAlign: 'left' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 10, color: '#64748b', userSelect: 'none' }}>{isExpanded ? '▼' : '▶'}</span>
                        <span style={{ ...mono, color: '#0b318bff' }}>{row.po_number || '—'}</span><div style={{ fontSize: 10.5, fontWeight: 600, color: '#94a3b8', fontFamily: 'var(--font)' }}>Serial {row.serial_number}</div>
                      </div>
                    </td>
                    {/* 2 — CREATED DATE */}
                    <td style={{ ...tdB, color: '#64748b' }}>{fmtDate(row.created_at)}</td>
                    {/* 3 — SUBMITTED DATE */}
                    <td style={{ ...tdB, color: '#64748b' }}>{fmtDate(row.submitted_date)}</td>
                    {/* 4 — PROJECT */}
                    <td style={tdB}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.project_name || '—'}</div>
                      {row.project_code && <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.project_code}</div>}
                    </td>
                    {/* 5 — SUPPLIER */}
                    <td style={{ ...tdB, color: '#475569' }}>{row.supplier_name || '—'}</td>
                    {/* 6 — GRAND TOTAL */}
                    <td style={{ ...tdB, ...mono, color: '#0b318bff' }}>{fmt(row.grand_total)}</td>
                    {/* 7 — STATUS */}
                    <td style={tdB}>
                      <MPOStatusPill status={row.status} />
                    </td>
                    {/* 8 — CURRENTLY WITH */}
                    <td style={{ ...tdB, color: '#475569' }}>{row.currently_with || '—'}</td>
                    {/* 9 — ACTIONS */}
                    <td style={{ ...tdB, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {/* Eye — always */}
                        <button
                          onClick={(e) => openDetail(e, row)}
                          title="View detail"
                          style={{ background: 'transparent', color: '#0b318bff', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        >
                          <IcoEye />
                        </button>
                        {/* Edit — draft or returned only */}
                        {isEditable && (
                          <button
                            onClick={(e) => openEdit(e, row)}
                            title={row.status === 'returned' ? 'Correct and resubmit' : 'Edit PO'}
                            style={{ background: 'transparent', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <IcoPencil />
                          </button>
                        )}
                        {/* Print + PDF — submitted but not rejected */}
                        {showPrintPdf && <MPOPrintButton poId={row.po_id} />}
                        {showPrintPdf && <MPODownloadPDFButton poId={row.po_id} poNumber={row.po_number || row.serial_number} />}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr key={`exp-${row.po_id}`}>
                      <td colSpan={9} style={{ padding: 0, border: '1px solid black' }}>
                        <PinnedDetail width={tableWidth}>
                          {poLoading[row.po_id] ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', color: '#64748b', fontSize: 13 }}>
                              <Spinner size={16} /> Loading purchase order details…
                            </div>
                          ) : (
                            <ExpandedDetail
                              po={poCache[row.po_id] || row}
                              fullPO={poCache[row.po_id]}
                              loading={false}
                            />
                          )}
                        </PinnedDetail>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && !loading && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
          {filterStatus
            ? `${totalCount} order${totalCount !== 1 ? 's' : ''} with status "${MY_ORDERS_STATUS_OPTIONS.find(o => o.value === filterStatus)?.label || filterStatus}"`
            : `${totalCount} order${totalCount !== 1 ? 's' : ''} total`}
        </div>
      )}

      {totalCount > 10 && (
        <Pagination total={totalCount} skip={page.skip} limit={page.limit} onChange={setPage} />
      )}
    </div>
  );
}
